import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FilePlus2, Inbox, Loader2, Plus, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { FILE_REQUEST_STATUSES, matterForRequest, normaliseRequestStatus } from "@/lib/fileRequests";
import { createManualRequest, fetchFileRequests, saveRequestNotes, saveRequestStatus } from "@/lib/fileRequestsClient";

const STATUS_STYLES = {
  "": "border-sky-200 bg-sky-50 text-sky-800",
  waiting_to_be_submitted_in_atlas: "border-amber-200 bg-amber-50 text-amber-900",
  pending_in_atlas: "border-amber-200 bg-amber-50 text-amber-900",
  aid_submitted: "border-violet-200 bg-violet-50 text-violet-800",
  aid_approved: "border-emerald-200 bg-emerald-50 text-emerald-800",
  aid_not_submitted: "border-rose-200 bg-rose-50 text-rose-800",
};

function formatDate(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "";
}

// New legal aid files, from the request email to a decision: the Legal Aid Status Monitor's
// list, beside the matters the requests become. Each links to its matter by client name.
export default function ApplicationsPanel({ brand = "lacw", matters = [], onOpenMatter, onStartMatter, refreshKey = 0 }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [emailFeedMissing, setEmailFeedMissing] = useState(false);
  const [filter, setFilter] = useState("open");
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ clientName: "", aidType: "", dateLodged: "", status: "", notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchFileRequests(brand);
      setRequests(result.requests);
      setEmailFeedMissing(result.emailFeedMissing);
    } catch (err) {
      setError(err.message || "The file request list could not be reached.");
    } finally {
      setLoading(false);
    }
  }, [brand]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const counts = useMemo(() => {
    const byStatus = Object.fromEntries(FILE_REQUEST_STATUSES.map((option) => [option.value, 0]));
    for (const request of requests) byStatus[request.status] = (byStatus[request.status] || 0) + 1;
    return byStatus;
  }, [requests]);
  const openCount = requests.filter((request) => !["aid_approved", "aid_not_submitted"].includes(request.status)).length;

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return requests.filter((request) => {
      if (filter === "open" && ["aid_approved", "aid_not_submitted"].includes(request.status)) return false;
      if (filter !== "open" && filter !== "all" && request.status !== filter) return false;
      return !query || `${request.clientName} ${request.subject} ${request.notes}`.toLowerCase().includes(query);
    });
  }, [requests, filter, search]);

  const changeStatus = async (request, status) => {
    const previous = request.status;
    setRequests((current) => current.map((item) => (item.key === request.key ? { ...item, status } : item)));
    try {
      await saveRequestStatus(request, status);
    } catch (err) {
      setRequests((current) => current.map((item) => (item.key === request.key ? { ...item, status: previous } : item)));
      toast.error(err.message || "The status could not be saved.");
    }
  };

  const changeNotes = async (request, notes) => {
    if (notes === request.notes) return;
    setRequests((current) => current.map((item) => (item.key === request.key ? { ...item, notes } : item)));
    try {
      await saveRequestNotes(request, notes);
    } catch (err) {
      toast.error(err.message || "The notes could not be saved.");
    }
  };

  const addRequest = async (event) => {
    event.preventDefault();
    if (!form.clientName.trim()) return toast.error("Enter the client's name.");
    try {
      await createManualRequest({ ...form, clientName: form.clientName.trim() }, brand);
      toast.success(`File request for ${form.clientName.trim()} added.`);
      setForm({ clientName: "", aidType: "", dateLodged: "", status: "", notes: "" });
      setAdding(false);
      load();
    } catch (err) {
      toast.error(err.message || "The file request could not be added.");
    }
  };

  const filters = [
    { value: "open", label: "Open", count: openCount },
    ...FILE_REQUEST_STATUSES.map((option) => ({ value: option.value, label: option.label, count: counts[option.value] || 0 })),
    { value: "all", label: "All", count: requests.length },
  ];

  return (
    <section aria-labelledby="applications-heading" className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="p-5 border-b border-slate-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 id="applications-heading" className="flex items-center gap-2 text-lg font-bold text-slate-900"><Inbox className="h-5 w-5 text-purple-600" /> Applications</h2>
          <p className="mt-0.5 text-sm text-slate-500">New file requests, from the request email to VLA's decision. Uploading the application or approval letter above moves its request on.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setAdding((open) => !open)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:border-purple-300 hover:text-purple-700">
            <Plus className="h-4 w-4" /> Add a request
          </button>
          <button type="button" onClick={load} aria-label="Refresh file requests" className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:text-purple-700">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {adding && (
        <form onSubmit={addRequest} className="grid gap-3 border-b border-slate-100 bg-slate-50 p-5 sm:grid-cols-2 lg:grid-cols-5">
          <label className="text-sm"><span className="font-medium text-slate-700">Client name</span><input value={form.clientName} onChange={(e) => setForm({ ...form, clientName: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3" required /></label>
          <label className="text-sm"><span className="font-medium text-slate-700">Aid type</span><input value={form.aidType} onChange={(e) => setForm({ ...form, aidType: e.target.value })} placeholder="e.g. Summary crime" className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3" /></label>
          <label className="text-sm"><span className="font-medium text-slate-700">Date lodged</span><input type="date" value={form.dateLodged} onChange={(e) => setForm({ ...form, dateLodged: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3" /></label>
          <label className="text-sm"><span className="font-medium text-slate-700">Status</span><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3">{FILE_REQUEST_STATUSES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          <div className="flex items-end"><button type="submit" className="h-10 w-full rounded-lg bg-purple-600 px-4 text-sm font-semibold text-white hover:bg-purple-700">Add request</button></div>
          <label className="text-sm sm:col-span-2 lg:col-span-5"><span className="font-medium text-slate-700">Notes</span><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3" /></label>
        </form>
      )}

      <div className="flex flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {filters.map((option) => (
            <button key={option.value || "new"} type="button" onClick={() => setFilter(option.value)} className={`rounded-full border px-3 py-1 text-xs font-semibold ${filter === option.value ? "border-purple-600 bg-purple-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-purple-300"}`}>
              {option.label} <span className="opacity-70">{option.count}</span>
            </button>
          ))}
        </div>
        <label className="relative block lg:w-72"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client or subject" className="h-9 w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm" /></label>
      </div>

      {error ? (
        <p role="alert" className="p-6 text-sm text-rose-700">{error}</p>
      ) : loading && requests.length === 0 ? (
        <p className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading file requests…</p>
      ) : (
        <>
          {emailFeedMissing && <p className="border-b border-amber-100 bg-amber-50 px-5 py-2 text-xs font-semibold text-amber-900">The emailed file requests could not be loaded just now; only requests added by hand are shown.</p>}
          {visible.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">No file requests here.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {visible.map((request) => {
                const matter = matterForRequest(request, matters);
                return (
                  <li key={request.key} className="grid gap-3 p-4 md:grid-cols-[minmax(180px,1fr)_minmax(200px,1.4fr)_190px_minmax(160px,1fr)_auto] md:items-start">
                    <div>
                      <p className="font-bold text-slate-900">{request.clientName}</p>
                      <p className="text-xs text-slate-500">Received {formatDate(request.date) || "—"}{request.isManual ? " · added by hand" : ""}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-800" title={request.subject}>{request.subject}</p>
                      <p className="truncate text-xs text-slate-500" title={request.snippet}>{request.snippet}</p>
                    </div>
                    <select aria-label={`Status for ${request.clientName}`} value={normaliseRequestStatus(request.status)} onChange={(e) => changeStatus(request, e.target.value)} className={`h-9 rounded-lg border px-2 text-xs font-semibold ${STATUS_STYLES[request.status] || STATUS_STYLES[""]}`}>
                      {FILE_REQUEST_STATUSES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <input key={`${request.key}:${request.notes}`} aria-label={`Notes for ${request.clientName}`} defaultValue={request.notes} onBlur={(e) => changeNotes(request, e.target.value)} placeholder="Notes" className="h-9 rounded-lg border border-slate-200 px-2 text-xs" />
                    <div className="md:text-right">
                      {matter ? (
                        <button type="button" onClick={() => onOpenMatter?.(matter)} className="rounded-lg bg-purple-50 px-3 py-2 text-xs font-bold text-purple-700 hover:bg-purple-100">Open matter</button>
                      ) : (
                        <button type="button" onClick={() => onStartMatter?.(request)} title={`No Aid Planner matter for ${request.clientName} yet`} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 hover:border-purple-300 hover:text-purple-700"><FilePlus2 className="h-3.5 w-3.5" /> Start matter</button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="border-t border-slate-100 px-5 py-2 text-[11px] text-slate-400">Showing {visible.length} of {requests.length} file requests</p>
        </>
      )}
    </section>
  );
}
